import { Component, OnDestroy, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators, AbstractControl } from '@angular/forms';
import { UpdateDeviceRequest } from './devices.service';
import { Device } from './bots.component';

@Component({
  selector: 'app-edit-bot-dialog',
  templateUrl: './edit-bot-dialog.component.html',
  styleUrls: ['./edit-bot-dialog.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  standalone: true,
})
export class EditBotDialogComponent implements OnDestroy {
  editBotForm: FormGroup;
  imagePreview: string | null = null;
  selectedImageFile: File | null = null;
  private isExistingImageUrl = false;

  constructor(
    public dialogRef: MatDialogRef<EditBotDialogComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public device: Device,
  ) {
    this.editBotForm = this.fb.group({
      name: [device.name, Validators.required],
      cameras: this.fb.array([]),
    });

    const isRealUrl = device.mainPhotoUrl && /^https?:\/\/|^\//.test(device.mainPhotoUrl);
    if (isRealUrl) {
      this.imagePreview = device.mainPhotoUrl;
      this.isExistingImageUrl = true;
    }

    device.cockpits.forEach(camera => this.addExistingCamera(camera));
  }

  get cameras(): FormArray {
    return this.editBotForm.get('cameras') as FormArray;
  }

  private addExistingCamera(camera: { id: string; name: string }): void {
    this.cameras.push(this.fb.group({
      id: [camera.id],
      name: [camera.name, Validators.required],
    }));
  }

  addCamera(): void {
    this.cameras.push(this.fb.group({
      id: [null],
      name: ['', Validators.required],
    }));
  }

  removeCamera(index: number): void {
    this.cameras.removeAt(index);
  }

  getCameraControl(index: number): AbstractControl | null {
    return this.cameras.at(index);
  }

  onImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      this.selectedImageFile = file;
      if (this.imagePreview && !this.isExistingImageUrl) {
        URL.revokeObjectURL(this.imagePreview);
      }
      this.imagePreview = URL.createObjectURL(file);
      this.isExistingImageUrl = false;
    }
  }

  removeImage(): void {
    if (this.imagePreview && !this.isExistingImageUrl) {
      URL.revokeObjectURL(this.imagePreview);
    }
    this.imagePreview = null;
    this.selectedImageFile = null;
    this.isExistingImageUrl = false;
  }

  ngOnDestroy(): void {
    if (this.imagePreview && !this.isExistingImageUrl) {
      URL.revokeObjectURL(this.imagePreview);
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onSubmit(): void {
    this.editBotForm.markAllAsTouched();
    if (this.editBotForm.valid) {
      const formValue = this.editBotForm.value;

      const deviceData: UpdateDeviceRequest = {
        name: formValue.name,
        cameras: formValue.cameras.map((c: { id: string | null; name: string }) => ({
          ...(c.id !== null && { id: c.id }),
          name: c.name,
        })),
        ...(this.selectedImageFile && { imageFile: this.selectedImageFile }),
      };

      this.dialogRef.close({ submitted: true, deviceData });
    }
  }
}
